import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AA_CATEGORIES } from "@/lib/askAnything";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export function AskTemplatesDialog({ open, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => { try { setTemplates((await api.get("/admin/ask-anything/templates")).data.templates); } catch {} };
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    if (!title.trim() || !bodyText.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/ask-anything/templates", { title: title.trim(), body: bodyText.trim(), category: category || null });
      setTitle(""); setCategory(""); setBodyText(""); await load(); toast.success("Template saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (tid) => {
    try { await api.delete(`/admin/ask-anything/templates/${tid}`); await load(); toast.success("Template deleted."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="aa-templates-dialog">
        <DialogHeader>
          <DialogTitle>Saved reply templates</DialogTitle>
          <DialogDescription>Reusable, on-brand replies your team can insert into any ticket.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 border border-kid-line rounded-xl p-4">
          <div className="flex items-center gap-2 text-kid-navy font-bold text-sm"><Plus size={15} className="text-kid-teal" /> New template</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="IEP prep welcome" data-testid="tpl-title" className="h-10 rounded-xl" /></div>
            <div className="space-y-1"><Label>Category (optional)</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="tpl-category" className="h-10 w-full rounded-xl border border-kid-line px-2 bg-white text-sm">
                <option value="">Any</option>
                {AA_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1"><Label>Body</Label><Textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={4} placeholder="Thank you for reaching out…" data-testid="tpl-body" className="rounded-xl resize-none" /></div>
          <button onClick={create} disabled={saving || !title.trim() || !bodyText.trim()} className="kid-btn kid-btn-primary h-10 px-5 disabled:opacity-50" data-testid="tpl-save">{saving ? "Saving…" : "Save template"}</button>
        </div>

        <div className="space-y-2 mt-2" data-testid="tpl-list">
          {templates.length === 0 ? <p className="text-sm text-kid-muted text-center py-4">No templates yet.</p> :
            templates.map((t) => (
              <div key={t.id} className="flex items-start justify-between gap-3 bg-kid-mist rounded-xl p-3" data-testid="tpl-item">
                <div className="min-w-0">
                  <p className="font-semibold text-kid-navy text-sm">{t.title}</p>
                  <p className="text-xs text-kid-muted truncate">{t.body}</p>
                </div>
                <button onClick={() => remove(t.id)} className="text-kid-coral p-1.5 rounded-lg hover:bg-white shrink-0" data-testid="tpl-delete"><Trash2 size={15} /></button>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
